/**
 * AudioMixer
 *
 * Manages a tree of AudioBus instances.
 * Provides master output, bus routing, solo logic,
 * and serialization to/from AudioMixerDefinition.
 *
 * @module @engine/audio/AudioMixer
 */

import type {
  AudioMixerDefinition,
  AudioMixerBusDefinition,
  AudioBusConfig,
  AudioBusState,
  AudioMixerSnapshot,
  AudioFilterConfig,
} from './types';
import { AudioBus } from './AudioBus';

export class AudioMixer {
  private _ctx: AudioContext;
  private _buses: Map<string, AudioBus> = new Map();
  private _masterBusId: string = 'master';
  private _destination: AudioNode;
  private _snapshots: AudioMixerSnapshot[] = [];

  constructor(ctx: AudioContext, destination: AudioNode) {
    this._ctx = ctx;
    this._destination = destination;
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /** Get the master bus */
  get master(): AudioBus | undefined {
    return this._buses.get(this._masterBusId);
  }

  /** Get a bus by ID */
  getBus(id: string): AudioBus | undefined {
    return this._buses.get(id);
  }

  /** Get all bus IDs */
  get busIds(): string[] {
    return Array.from(this._buses.keys());
  }

  /** Get all buses */
  get buses(): AudioBus[] {
    return Array.from(this._buses.values());
  }

  /** Get all bus states for UI */
  getBusStates(): AudioBusState[] {
    return this.buses.map(b => b.getState());
  }

  /** Get the input node for a bus (for connecting audio sources) */
  getBusInput(busId: string): AudioNode | undefined {
    return this._buses.get(busId)?.input;
  }

  /** Get snapshots */
  get snapshots(): AudioMixerSnapshot[] {
    return this._snapshots;
  }

  /**
   * Initialize from a mixer definition.
   */
  loadDefinition(def: AudioMixerDefinition): void {
    this.clear();
    this._snapshots = def.snapshots.map(s => ({ ...s }));

    // Create all buses first
    for (const busDef of def.buses) {
      const config: AudioBusConfig = {
        id: busDef.id,
        name: busDef.name,
        volume: busDef.volume,
        muted: busDef.muted,
        solo: busDef.solo,
        parentBusId: busDef.parentBusId,
        sends: busDef.sends.map(s => ({ ...s })),
        filters: busDef.filters.map(f => ({ ...f })),
      };
      const bus = new AudioBus(this._ctx, config);
      this._buses.set(busDef.id, bus);

      // Find master bus
      if (busDef.parentBusId === null) {
        this._masterBusId = busDef.id;
      }
    }

    // Wire up hierarchy
    this._rewire();

    // Set up sends
    for (const busDef of def.buses) {
      const bus = this._buses.get(busDef.id);
      if (!bus) continue;

      for (const sendDef of busDef.sends) {
        const targetBus = this._buses.get(sendDef.targetBusId);
        if (targetBus) {
          bus.addSend(sendDef.targetBusId, sendDef.level, sendDef.enabled, targetBus.input);
        }
      }
    }
  }

  /**
   * Create a default mixer with Master, Music, SFX, UI, and Ambient buses.
   */
  loadDefaults(): void {
    this.clear();

    const busDefs: AudioBusConfig[] = [
      { id: 'master', name: 'Master', volume: 0, muted: false, solo: false, parentBusId: null, sends: [], filters: [] },
      { id: 'music', name: 'Music', volume: -6, muted: false, solo: false, parentBusId: 'master', sends: [], filters: [] },
      { id: 'sfx', name: 'SFX', volume: 0, muted: false, solo: false, parentBusId: 'master', sends: [], filters: [] },
      { id: 'ui', name: 'UI', volume: 0, muted: false, solo: false, parentBusId: 'master', sends: [], filters: [] },
      { id: 'ambient', name: 'Ambient', volume: -3, muted: false, solo: false, parentBusId: 'master', sends: [], filters: [] },
    ];

    for (const config of busDefs) {
      this._buses.set(config.id, new AudioBus(this._ctx, config));
    }

    this._masterBusId = 'master';
    this._rewire();
  }

  /**
   * Add a new bus.
   */
  addBus(config: AudioBusConfig): AudioBus {
    const bus = new AudioBus(this._ctx, config);
    this._buses.set(config.id, bus);
    this._rewire();
    return bus;
  }

  /**
   * Remove a bus (cannot remove master).
   */
  removeBus(busId: string): boolean {
    if (busId === this._masterBusId) return false;
    const bus = this._buses.get(busId);
    if (!bus) return false;

    // Reparent children to master
    for (const child of this._buses.values()) {
      if (child.parentBusId === busId) {
        child.parentBusId = this._masterBusId;
      }
    }

    bus.destroy();
    this._buses.delete(busId);
    this._rewire();
    return true;
  }

  /**
   * Apply solo logic. When any bus is soloed, only soloed buses produce sound.
   */
  applySoloLogic(): void {
    const hasSolo = Array.from(this._buses.values()).some(b => b.solo);
    if (!hasSolo) {
      // No solo: respect individual mute settings
      for (const bus of this._buses.values()) {
        bus.muted = bus.muted; // triggers gain update
      }
      return;
    }

    // Solo mode: mute non-soloed buses (except master and ancestors of soloed buses)
    const soloedBuses = new Set<string>();
    for (const bus of this._buses.values()) {
      if (bus.solo) {
        soloedBuses.add(bus.id);
        // Also unmute ancestors up to master
        let parentId = bus.parentBusId;
        while (parentId) {
          soloedBuses.add(parentId);
          const parent = this._buses.get(parentId);
          parentId = parent?.parentBusId ?? null;
        }
      }
    }

    for (const bus of this._buses.values()) {
      if (bus.id === this._masterBusId) continue;
      bus.muted = !soloedBuses.has(bus.id);
    }
  }

  /**
   * Update metering for all buses.
   */
  updateMetering(): void {
    for (const bus of this._buses.values()) {
      bus.updateMetering();
    }
  }

  /**
   * Export the current state as a definition.
   */
  toDefinition(): AudioMixerDefinition {
    const buses: AudioMixerBusDefinition[] = [];

    for (const bus of this._buses.values()) {
      const config = bus.getConfig();
      buses.push({
        id: config.id,
        name: config.name,
        volume: config.volume,
        muted: config.muted,
        solo: config.solo,
        parentBusId: config.parentBusId,
        sends: config.sends,
        filters: config.filters,
        color: this._getDefaultBusColor(config.id),
      });
    }

    return {
      version: 1,
      name: 'Audio Mixer',
      buses,
      snapshots: this._snapshots.map(s => ({ ...s })),
    };
  }

  /**
   * Apply a snapshot instantly (no transition).
   */
  applySnapshot(snapshotId: string): void {
    this.transitionToSnapshot(snapshotId, 0);
  }

  /**
   * Smoothly transition to a snapshot over `durationOverride` seconds.
   * If `durationOverride` is undefined, uses the snapshot's own `transitionTime`.
   */
  transitionToSnapshot(snapshotId: string, durationOverride?: number): void {
    const snapshot = this._snapshots.find(s => s.id === snapshotId);
    if (!snapshot) return;

    const duration = durationOverride ?? snapshot.transitionTime;

    // Transition volumes
    for (const [busId, volume] of Object.entries(snapshot.busVolumes)) {
      const bus = this._buses.get(busId);
      if (bus) {
        bus.rampVolume(volume, duration);
      }
    }

    // Transition filters
    if (snapshot.busFilters) {
      for (const [busId, filters] of Object.entries(snapshot.busFilters)) {
        const bus = this._buses.get(busId);
        if (bus) {
          bus.transitionFilters(filters, duration);
        }
      }
    }
  }

  /**
   * Save current state as a snapshot (volumes + filters).
   */
  saveSnapshot(id: string, name: string, transitionTime: number = 0.5): void {
    const busVolumes: Record<string, number> = {};
    const busFilters: Record<string, AudioFilterConfig[]> = {};

    for (const bus of this._buses.values()) {
      busVolumes[bus.id] = bus.volume;
      const filters = bus.filters;
      if (filters.length > 0) {
        busFilters[bus.id] = [...filters];
      }
    }

    const snapshot: AudioMixerSnapshot = {
      id,
      name,
      busVolumes,
      busFilters: Object.keys(busFilters).length > 0 ? busFilters : undefined,
      transitionTime,
    };

    const existing = this._snapshots.findIndex(s => s.id === id);
    if (existing >= 0) {
      this._snapshots[existing] = snapshot;
    } else {
      this._snapshots.push(snapshot);
    }
  }

  /**
   * Clear all buses.
   */
  clear(): void {
    for (const bus of this._buses.values()) {
      bus.destroy();
    }
    this._buses.clear();
    this._snapshots = [];
  }

  /**
   * Destroy the mixer.
   */
  destroy(): void {
    this.clear();
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  /**
   * Rewire all bus connections based on parent hierarchy.
   */
  private _rewire(): void {
    // Disconnect all
    for (const bus of this._buses.values()) {
      bus.disconnect();
    }

    // Connect master to destination
    const master = this._buses.get(this._masterBusId);
    if (master) {
      master.connectTo(this._destination);
    }

    // Connect children to their parents
    for (const bus of this._buses.values()) {
      if (bus.id === this._masterBusId) continue;
      const parentId = bus.parentBusId ?? this._masterBusId;
      const parent = this._buses.get(parentId);
      if (parent) {
        bus.connectTo(parent.input);
      }
    }
  }

  private _getDefaultBusColor(busId: string): string {
    const colors: Record<string, string> = {
      master: '#e2e8f0',
      music: '#f59e0b',
      sfx: '#ef4444',
      ui: '#3b82f6',
      ambient: '#10b981',
    };
    return colors[busId] ?? '#8b5cf6';
  }
}
